import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tasksApi } from '@/api/tasks';
import type { TaskDetail, DeliveryType } from '@/types';
import { RejectModal } from '@/components/RejectModal';
import { OtpModal } from '@/components/OtpModal';
import { SUPPORT_PHONE } from '@/utils/env';
import { playNewTaskSound } from '@/utils/notificationSound';

const DETAIL_POLL_INTERVAL = 15_000;

function openMaps(lat: number, lng: number) {
  window.open(
    `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
    '_blank'
  );
}

function callPhone(phone: string) {
  window.location.href = `tel:${phone}`;
}

export function TaskDetailPage() {
  const { type, taskId } = useParams<{ type: string; taskId: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);

  const deliveryType: DeliveryType = type === 'cloth' ? 'CLOTH' : 'FOOD';

  const fetchDetail = useCallback(async () => {
    if (!taskId || !type) return;
    setError('');
    try {
      const res = await tasksApi.getTaskDetail(deliveryType, taskId);
      if (res.data) setTask(res.data.task);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load task');
    } finally {
      setLoading(false);
    }
  }, [taskId, type, deliveryType]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Refetch when tab becomes visible again
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchDetail();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchDetail]);

  // Poll for status updates (e.g. cancelled by vendor/customer)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevStatusRef = useRef<string | null>(null);

  useEffect(() => {
    if (!task) return;
    prevStatusRef.current = task.status;

    const isTerminal = ['DELIVERED', 'CANCELLED', 'FAILED'].includes(task.status);
    if (isTerminal) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }

    pollRef.current = setInterval(async () => {
      try {
        const res = await tasksApi.getTaskDetail(deliveryType, taskId!);
        if (res.data) {
          const updated = res.data.task;
          // Notify rider if task was cancelled
          if (updated.status === 'CANCELLED' && prevStatusRef.current !== 'CANCELLED') {
            playNewTaskSound();
          }
          prevStatusRef.current = updated.status;
          setTask(updated);
        }
      } catch {
        // ignore poll errors
      }
    }, DETAIL_POLL_INTERVAL);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [task?.status, deliveryType, taskId]);

  const handleAccept = async () => {
    if (!taskId) return;
    setActionLoading(true);
    try {
      await tasksApi.acceptTask(deliveryType, taskId);
      await fetchDetail();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept');
      await fetchDetail(); // Refetch to get updated status (e.g. cancelled)
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (reason: string) => {
    if (!taskId) return;
    setActionLoading(true);
    try {
      await tasksApi.rejectTask(deliveryType, taskId, reason);
      setShowRejectModal(false);
      navigate('/tasks', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePickup = async () => {
    if (!taskId) return;
    setActionLoading(true);
    try {
      await tasksApi.pickupTask(deliveryType, taskId);
      await fetchDetail();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
      await fetchDetail(); // Refetch to get updated status (e.g. cancelled)
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeliver = async (otp: string) => {
    if (!taskId) return;
    setActionLoading(true);
    try {
      await tasksApi.deliverTask(deliveryType, taskId, otp);
      setShowOtpModal(false);
      await fetchDetail();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid OTP');
      await fetchDetail(); // Refetch to get updated status (e.g. cancelled)
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-center" style={{ minHeight: '100dvh' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (error && !task) {
    return (
      <div className="container" style={{ paddingTop: 32 }}>
        <div className="error-state">{error}</div>
        <button className="btn btn-outline btn-full" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  if (!task) return null;

  const isNew = task.status === 'CREATED';
  const isAssigned = task.status === 'DRIVER_ASSIGNED' || task.status === 'PICKUP_ARRIVED';
  const isPickedUp = task.status === 'PICKED_UP' || task.status === 'OUT_FOR_DELIVERY';
  const isDelivered = task.status === 'DELIVERED';
  const isFinished = task.status === 'DELIVERED' || task.status === 'CANCELLED' || task.status === 'FAILED';

  return (
    <div className="container" style={{ paddingTop: 8, paddingBottom: 100 }}>
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        style={{
          background: 'none',
          color: 'var(--primary)',
          fontSize: 14,
          fontWeight: 600,
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        ← Back
      </button>

      {/* Status header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {task.type === 'FOOD' ? '🍔 Food' : '👕 Clothing'} Delivery
          </p>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>{task.vendorName}</h1>
        </div>
        <span
          className={`badge ${
            isNew
              ? 'badge-created'
              : isAssigned
                ? 'badge-assigned'
                : isPickedUp
                  ? 'badge-picked'
                  : isDelivered
                    ? 'badge-delivered'
                    : 'badge-cancelled'
          }`}
        >
          {task.status.replace(/_/g, ' ')}
        </span>
      </div>

      {error && (
        <div style={{ color: 'var(--danger)', fontSize: 14, marginBottom: 12 }}>{error}</div>
      )}

      {task.status === 'CANCELLED' && (
        <div
          style={{
            padding: '12px 16px',
            background: 'var(--danger-light)',
            borderRadius: 'var(--radius-sm)',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div>
            <p style={{ fontWeight: 600, color: '#991b1b', fontSize: 14 }}>Order Cancelled</p>
            <p style={{ fontSize: 13, color: '#991b1b', opacity: 0.8 }}>
              This order has been cancelled. No further action needed.
            </p>
          </div>
        </div>
      )}

      {/* Pickup */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Pickup
            </p>
            <p style={{ fontSize: 14, marginTop: 4 }}>{task.pickupAddress}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-primary"
            style={{ fontSize: 13, padding: '10px 16px', minHeight: 40, flex: 1 }}
            onClick={() => openMaps(task.pickupLat, task.pickupLng)}
          >
            Navigate
          </button>
          {!isFinished && (
            <button
              className="btn btn-outline"
              style={{ fontSize: 13, padding: '10px 16px', minHeight: 40 }}
              onClick={() => callPhone(task.vendorPhone)}
            >
              Call Vendor
            </button>
          )}
        </div>
      </div>

      {/* Drop */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 8 }}>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Drop
          </p>
          <p style={{ fontSize: 14, marginTop: 4 }}>{task.dropAddress}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-primary"
            style={{ fontSize: 13, padding: '10px 16px', minHeight: 40, flex: 1 }}
            onClick={() => openMaps(task.dropLat, task.dropLng)}
          >
            Navigate
          </button>
          {!isFinished && (
            <button
              className="btn btn-outline"
              style={{ fontSize: 13, padding: '10px 16px', minHeight: 40 }}
              onClick={() => callPhone(task.customerPhone)}
            >
              Call Customer
            </button>
          )}
        </div>
      </div>

      {/* Items */}
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
          Items
        </p>
        {task.items.map((item, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: i < task.items.length - 1 ? '1px solid var(--border)' : 'none',
              fontSize: 14,
            }}
          >
            <span>
              {item.name} × {item.quantity}
            </span>
            <span style={{ fontWeight: 600 }}>₹{item.price * item.quantity}</span>
          </div>
        ))}
      </div>

      {/* Payment */}
      <div className="card" style={{ marginBottom: 24 }}>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
          Payment
        </p>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
          <span>Type</span>
          <span style={{ fontWeight: 600, color: task.paymentType === 'COD' ? 'var(--warning)' : 'var(--success)' }}>
            {task.paymentType}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginTop: 6 }}>
          <span>Total</span>
          <span style={{ fontWeight: 700, fontSize: 16 }}>₹{task.totalAmount}</span>
        </div>
        {task.paymentType === 'COD' && task.codAmount && (
          <div
            style={{
              marginTop: 10,
              padding: '10px 12px',
              background: 'var(--warning-light)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 14,
              fontWeight: 600,
              color: '#92400e',
            }}
          >
            Collect ₹{task.codAmount} from customer
          </div>
        )}
      </div>

      {/* Support */}
      <button
        className="btn btn-outline btn-full"
        style={{ marginBottom: 24, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
        onClick={() =>
          window.open(
            `https://wa.me/${SUPPORT_PHONE}?text=${encodeURIComponent(`Hi, I need help with delivery for ${task.vendorName}`)}`,
            '_blank'
          )
        }
      >
        💬 Need Help? Chat with Support
      </button>

      {/* Actions */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: 16,
          background: 'var(--bg-card)',
          borderTop: '1px solid var(--border)',
          maxWidth: 480,
          margin: '0 auto',
        }}
      >
        {isNew && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-danger btn-full"
              onClick={() => setShowRejectModal(true)}
              disabled={actionLoading}
            >
              Reject
            </button>
            <button
              className="btn btn-success btn-full"
              onClick={handleAccept}
              disabled={actionLoading}
            >
              {actionLoading ? 'Accepting...' : 'Accept'}
            </button>
          </div>
        )}

        {isAssigned && (
          <button
            className="btn btn-primary btn-full"
            onClick={handlePickup}
            disabled={actionLoading}
          >
            {actionLoading ? 'Updating...' : 'Mark Picked Up'}
          </button>
        )}

        {isPickedUp && (
          <button
            className="btn btn-success btn-full"
            onClick={() => setShowOtpModal(true)}
            disabled={actionLoading}
          >
            Mark Delivered
          </button>
        )}

        {isDelivered && (
          <div
            style={{
              textAlign: 'center',
              padding: 12,
              background: 'var(--success-light)',
              borderRadius: 'var(--radius-sm)',
              color: '#166534',
              fontWeight: 600,
            }}
          >
            Delivery Completed
          </div>
        )}
      </div>

      {showRejectModal && (
        <RejectModal
          onConfirm={handleReject}
          onClose={() => setShowRejectModal(false)}
          loading={actionLoading}
        />
      )}

      {showOtpModal && (
        <OtpModal
          onConfirm={handleDeliver}
          onClose={() => setShowOtpModal(false)}
          loading={actionLoading}
        />
      )}
    </div>
  );
}
